import React, { useState, useEffect, useMemo, useCallback } from "react";

import { useAuth } from "../context/AuthContext";
import { renderCategoryIcon } from "../utils/categoryIcons";
import ResponseDetailModal from "./ResponseDetailModal";
import {
  FileText,
  Download,
  Search,
  Eye,
  User,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { DataTable } from "./ui/data-table";
import { ColumnDef } from "@tanstack/react-table";
import "./SurveyResponses.css";
import { getBackendUrl } from "../config/env";

interface ResponseData {
  id: string;
  _id?: string;
  surveyId: string;
  surveyTitle?: string;
  respondentId: string;
  respondentName?: string;
  respondentEmail?: string;
  submittedAt: string;
  completedAt?: string;
  startedAt?: string;
  status: "completed" | "in_progress" | "abandoned";
  score?: number;
  answers: any[];
  metadata?: {
    browser?: string;
    os?: string;
    deviceType?: string;
  };
}

const SurveyResponses: React.FC = () => {

  const { user, centerInfo, selectedCenterId } = useAuth();
  const [responses, setResponses] = useState<ResponseData[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedSurvey, setSelectedSurvey] = useState<string>("all");
  const [categories, setCategories] = useState<any[]>([]);
  const [surveysWithCategories, setSurveysWithCategories] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedResponse, setSelectedResponse] = useState<ResponseData | null>(
    null
  );
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResponses, setTotalResponses] = useState(0);
  const [pageSize, setPageSize] = useState(5);

  const loadCategories = useCallback(async () => {
    try {
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;
      const url = centerUserId
        ? `${getBackendUrl()}/api/survey-categories?isActive=true&centerUserId=${centerUserId}`
                  : `${getBackendUrl()}/api/survey-categories?isActive=true`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  }, [selectedCenterId, user?.centerId, centerInfo?.centerId]);

  const loadSurveys = useCallback(async () => {
    try {
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;
      
      // 모든 설문조사를 가져오기 위해 limit을 크게 설정
      const params = new URLSearchParams({
        page: "1",
        limit: "1000", // 충분히 큰 수로 설정하여 모든 설문조사 가져오기
      });
      
      if (centerUserId) {
        params.append("centerUserId", centerUserId);
      }

      const surveysUrl = `${getBackendUrl()}/api/surveys?${params.toString()}`;
      
      console.log("🔍 SurveyResponses API 요청:", surveysUrl);
      
      const response = await fetch(surveysUrl);
      if (response.ok) {
        const data = await response.json();
        
        console.log("📊 받은 설문조사 데이터:", data);
        
        // 페이징 응답과 비-페이징 응답 모두 처리
        const surveys = data.data ? data.data : data;
        console.log("📋 처리된 설문조사 목록:", surveys);
        
        setSurveysWithCategories(surveys);
      }
    } catch (error) {
      console.error("Failed to load surveys:", error);
      setSurveysWithCategories([]); // 오류 시 빈 배열로 설정
    }
  }, [selectedCenterId, user?.centerId, centerInfo?.centerId]);

  const loadResponses = useCallback(async (page: number = 1, size?: number) => {
    // 설문조사를 선택하지 않았으면 응답을 로드하지 않음
    if (selectedSurvey === "all") {
      setResponses([]);
      setTotalPages(1);
      setTotalResponses(0);
      setCurrentPage(1);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;
      const actualPageSize = size ?? pageSize;

      // Load responses for specific survey with pagination
      const surveyId = selectedSurvey;
      const params = new URLSearchParams();
      if (centerUserId) params.append("centerUserId", centerUserId);
      params.append("page", page.toString());
      params.append("limit", actualPageSize.toString());
      if (searchTerm) params.append("search", searchTerm);

      const url = `${getBackendUrl()}/api/responses/survey/${surveyId}?${params.toString()}`;

      const response = await fetch(url);
      if (response.ok) {
        const result = await response.json();
        const surveyData = Array.isArray(surveysWithCategories) 
          ? surveysWithCategories.find((s) => (s.id || s._id) === surveyId)
          : null;

        if (result.data) {
          // 페이지네이션 응답
          const formattedResponses = result.data.map((r: any) => ({
            ...r,
            surveyTitle: surveyData?.title || "Unknown Survey",
          }));
          setResponses(formattedResponses);
          setTotalPages(result.totalPages || 1);
          setTotalResponses(result.total || 0);
          setCurrentPage(result.page || 1);
        }
      } else {
        // 오류 응답 처리
        console.error(`API Error (${response.status}):`, await response.text());
        setResponses([]);
        setTotalPages(1);
        setTotalResponses(0);
      }
    } catch (error) {
      console.error("Failed to load responses:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedCenterId, user?.centerId, centerInfo?.centerId, selectedSurvey, searchTerm, surveysWithCategories, pageSize]);

  useEffect(() => {
    loadCategories();
    loadSurveys();
  }, [loadCategories, loadSurveys]);

  useEffect(() => {
    if (Array.isArray(surveysWithCategories) && surveysWithCategories.length > 0) {
      setCurrentPage(1);
      loadResponses(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveysWithCategories, selectedSurvey, searchTerm, selectedCategory]); // pageSize는 handlePageSizeChange에서 처리

  useEffect(() => {
    if (selectedSurvey !== "all" && Array.isArray(surveysWithCategories)) {
      const selectedSurveyData = surveysWithCategories.find(
        (s) => (s.id || s._id) === selectedSurvey
      );
      if (
        selectedSurveyData &&
        selectedCategory !== "all" &&
        selectedSurveyData.categoryId !== selectedCategory
      ) {
        setSelectedSurvey("all");
      }
    }
  }, [selectedCategory, surveysWithCategories, selectedSurvey]);

  // 페이지 변경 시 데이터 다시 로드
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    loadResponses(newPage);
  };

  // 페이지 크기 변경 시
  const handlePageSizeChange = (newPageSize: string) => {
    const size = parseInt(newPageSize, 10);
    setPageSize(size);
    setCurrentPage(1);
    loadResponses(1, size); // 새로운 페이지 크기로 직접 호출
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);

      // 쿼리 파라미터 구성
      const params = new URLSearchParams();
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;

      if (centerUserId) params.append("centerUserId", centerUserId);
      if (selectedSurvey !== "all") params.append("surveyId", selectedSurvey);
      if (selectedCategory !== "all")
        params.append("categoryId", selectedCategory);

      const url = `${getBackendUrl()}/api/responses/export/excel?${params.toString()}`;

      // 엑셀 파일 다운로드
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("엑셀 파일을 생성하는데 실패했습니다.");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `survey-responses-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert("엑셀 파일을 다운로드하는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            완료
          </Badge>
        );
      case "in_progress":
        return (
          <Badge variant="secondary">
            <Clock className="h-3 w-3 mr-1" />
            진행중
          </Badge>
        );
      case "abandoned":
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            중단됨
          </Badge>
        );
      default:
        return <Badge variant="outline">알 수 없음</Badge>;
    }
  };



  // Define columns for the DataTable
  const columns: ColumnDef<ResponseData>[] = useMemo(
    () => [
      {
        accessorKey: "surveyTitle",
        header: "설문조사",
        meta: { filterLabel: "설문조사" },
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">
              {row.getValue("surveyTitle") || "Unknown Survey"}
            </span>
          </div>
        ),
      },
      {
        id: "respondent",
        header: "응답자",
        meta: { filterLabel: "응답자" },
        cell: ({ row }) => {
          const response = row.original;
          return (
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="font-medium">
                  {response.respondentName || response.respondentId}
                </div>
                {response.respondentEmail && (
                  <div className="text-sm text-muted-foreground">
                    {response.respondentEmail}
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "status",
        header: "상태",
        meta: { filterLabel: "상태" },
        cell: ({ row }) => getStatusBadge(row.getValue("status")),
      },
      {
        id: "submittedAt",
        header: "제출일시",
        meta: { filterLabel: "제출일시" },
        cell: ({ row }) => {
          const response = row.original;
          const date =
            response.submittedAt || response.completedAt || response.startedAt;
          return (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>{formatDate(date || "")}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "score",
        header: "점수",
        meta: { filterLabel: "점수" },
        cell: ({ row }) => {
          const score = row.getValue("score") as number | undefined;
          return score !== undefined ? (
            <Badge variant="outline">{score}점</Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          );
        },
      },
      {
        id: "metadata",
        header: "기기 정보",
        meta: { filterLabel: "기기 정보" },
        cell: ({ row }) => {
          const metadata = row.original.metadata;
          if (!metadata)
            return <span className="text-muted-foreground">-</span>;

          return (
            <div className="text-sm">
              <div>{metadata.deviceType || "-"}</div>
              <div className="text-muted-foreground">
                {metadata.browser || "-"} / {metadata.os || "-"}
              </div>
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "작업",
        meta: { filterLabel: "작업" },
        cell: ({ row }) => {
          const response = row.original;
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedResponse(response);
                setShowDetailModal(true);
              }}
            >
              <Eye className="h-4 w-4 mr-1" />
              상세보기
            </Button>
          );
        },
      },
    ],
    []
  );

  // 서버에서 페이징된 데이터를 그대로 사용
  const filteredResponses = responses;

  // Get filtered surveys for dropdown (배열인지 확인)
  const filteredSurveys = Array.isArray(surveysWithCategories) 
    ? surveysWithCategories.filter((survey) => {
        if (selectedCategory === "all") return true;
        return survey.categoryId === selectedCategory;
      })
    : [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">응답 관리</h1>
          <p className="text-muted-foreground">
            설문조사 응답을 확인하고 분석하세요
          </p>
        </div>
        <Button onClick={handleExportExcel} disabled={loading}>
          <Download className="mr-2 h-4 w-4" />
          {loading ? "내보내는 중..." : "엑셀 다운로드"}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>필터</CardTitle>
          <CardDescription>
            카테고리와 설문조사를 선택하여 응답을 필터링하세요
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>카테고리</Label>
              <Select
                value={selectedCategory}
                onValueChange={setSelectedCategory}
              >
                <SelectTrigger>
                  <SelectValue placeholder="카테고리 선택" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      전체 카테고리
                    </div>
                  </SelectItem>
                  {categories.map((category) => (
                    <SelectItem
                      key={category.id || category._id}
                      value={category.id || category._id}
                    >
                      <div className="flex items-center gap-2">
                        {renderCategoryIcon(category.icon)}
                        {category.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>설문조사</Label>
              <Select value={selectedSurvey} onValueChange={setSelectedSurvey}>
                <SelectTrigger>
                  <SelectValue placeholder="설문조사 선택" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">설문조사 선택</SelectItem>
                  {filteredSurveys.map((survey) => (
                    <SelectItem
                      key={survey.id || survey._id}
                      value={survey.id || survey._id}
                    >
                      {survey.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>검색</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="응답자 이름, 이메일 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>응답 목록</CardTitle>
          <CardDescription>
            총 {totalResponses}개의 응답 (페이지 {currentPage}/{totalPages})
          </CardDescription>
        </CardHeader>
        <CardContent>
          {selectedSurvey === "all" ? (
            // 설문조사를 선택하지 않았을 때 안내 메시지
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">설문조사를 선택해주세요</p>
              <p className="text-sm">상단에서 설문조사를 선택하면 응답 목록이 표시됩니다</p>
            </div>
          ) : (
            <>
              <DataTable
                columns={columns}
                data={filteredResponses}
                searchKey=""
                showPagination={false}
                showColumnToggle={true}
                pageSize={1000}
              />

              {/* 커스텀 페이지네이션 */}
              {
            <div className="flex items-center justify-between mt-4 border-t pt-4">
              <div className="flex items-center gap-4">
                <div className="text-sm text-muted-foreground">
                  {totalResponses > 0 ? (
                    <>
                      {(currentPage - 1) * pageSize + 1} -{" "}
                      {Math.min(currentPage * pageSize, totalResponses)} /{" "}
                      {totalResponses}개 표시
                    </>
                  ) : (
                    <>데이터 없음</>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">페이지당</Label>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={handlePageSizeChange}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5개</SelectItem>
                      <SelectItem value="10">10개</SelectItem>
                      <SelectItem value="20">20개</SelectItem>
                      <SelectItem value="50">50개</SelectItem>
                      <SelectItem value="100">100개</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1 || loading}
                  >
                    처음
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                  >
                    이전
                  </Button>

                  {/* 페이지 번호들 */}
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      if (pageNum < 1 || pageNum > totalPages) return null;

                      return (
                        <Button
                          key={pageNum}
                          variant={
                            pageNum === currentPage ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                          className="w-10"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                  >
                    다음
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages || loading}
                  >
                    마지막
                  </Button>
                </div>
              )}
            </div>
          }
            </>
          )}
        </CardContent>
      </Card>

      {showDetailModal && selectedResponse && (
        <ResponseDetailModal
          response={selectedResponse}
          open={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedResponse(null);
          }}
        />
      )}
    </div>
  );
};

export default SurveyResponses;
